import {panelClosed, panelOpened} from './actions';
import {indexPanels, CycleChildren} from './panels';
import type {OnActionMap, Panel, Panels, PanelName, PanelPath} from './types';
import {wrap} from './types';
import {last} from './utils';

// given an arbitrary number of sets, with each set containing
// an arbitrary number of items of unknown type, this function
// returns a new array containing all the possible combinations
// we can create by taking one item from each set.
export const getPermutations = (...sets: Array<Array<any>>) => {
  const results: Array<any | Array<any>> = [];

  const extend = (array: Array<any>, set: number) => {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    for (let i = 0; i < sets[set].length; i++) {
      const permutation = [...array];
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      permutation.push(sets[set][i]);
      if (set === sets.length - 1) {
        results.push(permutation);
      } else if (set < sets.length - 1) {
        extend(permutation, set + 1);
      }
    }
  };

  extend([], 0);

  return results;
};

export const indexTestPanels = indexPanels;

type TestPanelConfig = Omit<Panel<any>['metadata'], 'abilityName'> &
  Panel<any>['lifecycles'] & {
    abilityName?: string;
    placeholders?: boolean;
    Panel?: Panel<any>['Panel'];
  };

export const testPanel = (
  name: string,
  {
    onAction,
    onEnterRequested,
    onEnterCompleted,
    onExitRequested,
    onExitCompleted,
    Panel: PanelComponent = () => null,
    placeholders = true,
    ...metadata
  }: TestPanelConfig = {},
  children?: Array<Panel<any>>
): Panel<any> =>
  ({
    name,
    children,
    metadata,
    lifecycles: {
      onAction,
      onEnterRequested:
        onEnterRequested ||
        (placeholders ? async () => `enter${name.toUpperCase()}` : undefined),
      onEnterCompleted:
        onEnterCompleted ||
        (placeholders
          ? () => `enter${name.toUpperCase()}Completed`
          : undefined),
      onExitRequested:
        onExitRequested ||
        (placeholders ? async () => `exit${name.toUpperCase()}` : undefined),
      onExitCompleted:
        onExitCompleted ||
        (placeholders ? () => `exit${name.toUpperCase()}Completed` : undefined),
    },
    Panel: PanelComponent,
  }) as Panel<any>;

export const expandLifecycles = async (key: string, obj: any) => {
  if (obj && obj[key]) {
    const blockers = await Promise.all(
      // @ts-expect-error - TS7006 - Parameter 'fn' implicitly has an 'any' type.
      (obj[key].blockers || []).map((fn) => fn())
    );
    const sideEffects = await Promise.all(
      // @ts-expect-error - TS7006 - Parameter 'fn' implicitly has an 'any' type.
      (obj[key].sideEffects || []).map((fn) => fn())
    );

    return {...obj, [key]: {blockers, sideEffects}};
  }

  return obj;
};

export const resolveInOriginalOrder = (tasks: Array<() => Promise<any>>) =>
  tasks.reduce((promise, next) => promise.then(next), Promise.resolve());

export const panelConfigs = [
  testPanel('target', {abilityName: 'someAbility'}),
  testPanel('other'),
  testPanel('special', undefined, [
    testPanel('childOne', {parameter: 'parent'}, [
      testPanel('subChild', {
        onAction: {
          OPEN_SUBCHILD: () => panelOpened('subChild'),
          CLOSE_SUBCHILD: () => panelClosed('subChild'),
          DO_NOTHING: () => false,
        },
      }),
    ]),
    testPanel('childTwo'),
    testPanel('childThree'),
  ]),
  testPanel('cycle', {Panel: CycleChildren}, [
    testPanel('cycleChildOne', undefined, [testPanel('cycleSubChild')]),
    testPanel('cycleChildTwo'),
    testPanel('cycleChildThree'),
  ]),
  testPanel('params1', undefined, [
    testPanel('params2', {parameter: 'parent'}, [
      testPanel('params3', {parameter: 'child'}),
    ]),
  ]),
  testPanel('conditional', undefined, [
    testPanel('alwaysAvailable'),
    testPanel('abilityName', {abilityName: 'test'}, [
      testPanel('abilityChild'),
    ]),
  ]),
  testPanel('allAbilities', undefined, [
    testPanel('canAccessProjectSettings', {
      abilityName: 'canAccessProjectSettings',
    }),
    testPanel('canAccessInterfaceSettings', {
      abilityName: 'canAccessInterfaceSettings',
    }),
    testPanel('canManageEcomm/commerce', {abilityName: 'canManageEcomm'}),
    testPanel('canAccessBackups', {abilityName: 'canAccessBackups'}),
    testPanel('canAccessEmailSettings/email', {
      abilityName: 'canAccessEmailSettings',
    }),
    testPanel('canAccessLocalization', {abilityName: 'canAccessLocalization'}),
  ]),

  // matches designer panels
  testPanel('activityLog'),
  testPanel('add', undefined, [
    testPanel('addElement'),
    testPanel('addFromLibraries'),
  ]),
  testPanel('analytics-optimization', undefined, [
    testPanel('analytics-optimization-loading'),
    testPanel('analytics-optimization-settings'),
    testPanel('analytics-upsell'),
    testPanel('audiences'),
    testPanel('data-explorer'),
    testPanel('experiences'),
    testPanel('insights-discovery-panel'),
    testPanel('optimization-integration'),
    testPanel('optimization-overview'),
    testPanel('optimization-upsell'),
    testPanel('page_insights', undefined, [testPanel('page_insights_details')]),
    testPanel('site_overview'),
    testPanel('site_goals', undefined, [testPanel('goal_reporting')]),
  ]),
  testPanel('apps', undefined, [
    testPanel('apps/app'),
    testPanel('apps/marketplaceApp'),
  ]),
  testPanel('assets'),
  testPanel('auditor'),
  testPanel('cms/cms', undefined, [
    testPanel('cms/cms/collection/edit', undefined, [
      testPanel('cms/cms/collection/edit/preview'),
    ]),
    testPanel('cms/cms/csv'),
    testPanel('cms/cms/empty'),
    testPanel('cms/cms/item/edit'),
    testPanel('cms/cms/items'),
  ]),
  testPanel('commerce/cms', undefined, [
    testPanel('commerce/cms/collection/edit', undefined, [
      testPanel('commerce/cms/collection/edit/preview'),
    ]),
    testPanel('commerce/cms/csv'),
    testPanel('commerce/cms/empty'),
    testPanel('commerce/cms/item/edit'),
    testPanel('commerce/cms/items'),
    testPanel('commerce/discounts', undefined, [
      testPanel('commerce/discounts/edit'),
    ]),
    testPanel('commerce/gettingStarted'),
    testPanel('commerce/orders'),
    testPanel('commerce/subscriptions'),
  ]),
  testPanel('finderIcon'),
  testPanel('flows', undefined, [
    testPanel('flows/flows', undefined, [testPanel('flows/flows/authoring')]),
    testPanel('flows/values'),
  ]),
  testPanel('help'),
  testPanel('interactions'),
  testPanel('libraries'),
  testPanel('navigator'),
  testPanel('orion-create'),
  testPanel('orion-navigator'),
  testPanel('styles'),
  testPanel('theme'),
  testPanel('variables', undefined, [
    testPanel('siteVariables'),
    testPanel('libraryVariables'),
  ]),
  testPanel('pages', undefined, [testPanel('pageSettings')]),
  testPanel('siteSettings', undefined, [
    testPanel('settings/commerce', {abilityName: 'canManageEcomm'}),
    testPanel('settings/email', {abilityName: 'canAccessEmailSettings'}),
    testPanel('search'),
    testPanel('versions', {abilityName: 'canAccessBackups'}),
    testPanel('localization', {abilityName: 'canAccessLocalizationSettings'}),
    testPanel('interface'),
    testPanel('gsapIntegration', {
      abilityName: 'canAccessGsapIntegrationSettings',
    }),
    testPanel('featureConfig', {
      abilityName: 'canAccessFeatureConfigDashboard',
    }),
  ]),
  testPanel('symbols'),
  testPanel('tutorials'),
  testPanel('aeo-recommendations', {
    abilityName: 'canAccessAeoSiteAudit',
  }),
];

export const panels = indexPanels(panelConfigs);

// this is for use within the panels package tests
export const getState = ({
  docked = null,
  dockOpen = true,
  left = null,
  right = null,
  pending,
  customPanels,
  parameters = {},
}: {
  canDock?: boolean;
  docked?: PanelName | null;
  dockOpen?: boolean;
  left?: PanelName | null;
  right?: PanelName | null;
  pending?: any;
  customPanels?: Panels;
  parameters?: {
    [key: string]: any;
  };
} = {}) =>
  wrap({
    paneState: {
      docked: {
        panelName: docked,
        isOpen: dockOpen,
      },
      left: {panelName: left},
      right: {panelName: right},
    },
    panelConfigs,
    panels: customPanels || panels,
    parameters,
    pending,
  });

// this is for external use outside the panels package
export const createTestPanelsStore = ({
  docked = null,
  dockOpen = true,
  left = null,
  right = null,
  metadataByPanel,
}: {
  docked?: PanelPath | null;
  dockOpen?: boolean;
  left?: PanelPath | null;
  right?: PanelPath | null;
  /**
   * Metadata to merge into the synthetic panel of that name, for tests that
   * exercise a consumer reading `metadata` off the panel registry (e.g.
   * `suppressesAiAssistant`, `suppressesPageContext`). The real panel configs
   * are not consulted, so a test must declare the flags it depends on.
   */
  metadataByPanel?: Record<string, Partial<Panel<any>['metadata']>>;
  onAction?: OnActionMap;
  isPanelAvailable?: (arg1: Panel<any>) => boolean;
} = {}) => {
  const _panels = [docked, left, right].reduce(
    (acc, path) => {
      if (path) {
        const panel = path.reduceRight(
          // @ts-expect-error - TS2769 - No overload matches this call.
          (children, name) => ({
            name,
            // `indexPanels` reduces over `children`, so a nested path has to
            // build an array rather than the bare child object.
            children: children ? [children] : undefined,
            Panel: undefined,
            metadata: {
              panelPrefix: name.split('/')[0],
              ...metadataByPanel?.[name],
            },
          }),
          undefined
        );
        if (panel) {
          // @ts-expect-error - TS2352 - Conversion of type 'string' to type 'Panel<any>' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
          acc.push(panel as Panel<any>);
        }
      }

      return acc;
    },
    [...panelConfigs]
  );

  return wrap({
    paneState: {
      docked: {
        // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | null'.
        panelName: last(docked),
        isOpen: dockOpen,
      },
      // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | null'.
      left: {panelName: last(left)},
      // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | null'.
      right: {panelName: last(right)},
    },
    panels: indexPanels(_panels as any),
    parameters: {},
    panelConfigs: _panels,
  });
};
